import { defineMessages } from 'react-intl'

export const messagingMessages = defineMessages({
  activeBotChip: {
    defaultMessage: 'Bot: {name}',
    description:
      'messaging context — read-only chip showing the conversation’s currently-active playbook bot',
  },
  activeBotNone: {
    defaultMessage: 'No bot active',
    description:
      'messaging context — read-only chip when no playbook bot is active on the conversation',
  },
  aiFilterLabel: {
    defaultMessage: 'AI-handled',
    description: 'messaging inbox filter — conversations owned by the bot',
  },
  aiHandledCount: {
    defaultMessage: '{count} AI-handled',
    description: 'messaging topbar — bot-owned conversation count',
  },
  aiHandlingChip: {
    defaultMessage: 'AI handling',
    description: 'messaging thread header — live bot ownership chip',
  },
  aiIntakeChip: {
    defaultMessage: 'AI intake',
    description: 'messaging thread header — bot lead-capture (intake) chip',
  },
  aiScreeningChip: {
    defaultMessage: 'AI screening',
    description: 'messaging thread header — bot intake screening chip',
  },
  aiShortLabel: {
    defaultMessage: 'AI',
    description: 'messaging — short AI marker (list chip, preview prefix)',
  },
  archiveAction: {
    defaultMessage: 'Archive',
    description: 'messaging thread header — archive conversation action',
  },
  archiveBody: {
    defaultMessage:
      'Archiving is permanent: the conversation becomes view-only, no more messages can be sent, and the next message from this customer starts a brand-new conversation with no connection to this one. Any messages still waiting to send will fail, and linked cases will be unlinked (each keeps a read-only link back to this thread).',
    description: 'messaging archive confirm dialog body',
  },
  archiveConfirm: {
    defaultMessage: 'Archive conversation',
    description: 'messaging archive confirm dialog button',
  },
  archiveReasonHelp: {
    defaultMessage:
      'Optional. Recorded with your name so the next person can see why this thread was archived.',
    description: 'messaging archive reason helper',
  },
  archiveReasonLabel: {
    defaultMessage: 'Reason',
    description: 'messaging archive reason field label',
  },
  archiveSuccess: {
    defaultMessage: 'Conversation archived.',
    description: 'messaging archive success notification',
  },
  archiveTitle: {
    defaultMessage: 'Archive this conversation?',
    description: 'messaging archive confirm dialog title',
  },
  archivedBadge: {
    defaultMessage: 'Archived',
    description: 'messaging — archived conversation marker (inbox row chip, thread header)',
  },
  archivedReadOnly: {
    defaultMessage:
      'Archived — view only. New messages from this customer start a new conversation.',
    description: 'messaging thread — read-only footer on an archived conversation',
  },
  attachMenuImage: {
    defaultMessage: 'Photo / image',
    description: 'messaging composer attach menu — image option',
  },
  attachmentFailed: {
    defaultMessage: 'Attachment upload failed — please try again',
    description: 'messaging composer attachment upload error',
  },
  attachmentFallback: {
    defaultMessage: 'Attachment',
    description: 'messaging reply/quote fallback when a message has no text',
  },
  attachmentUploading: {
    defaultMessage: 'Uploading attachment…',
    description: 'messaging composer attachment upload in progress',
  },
  authorCustomer: {
    defaultMessage: 'Customer',
    description: 'messaging reply/quote author — inbound',
  },
  authorOperator: {
    defaultMessage: 'Agent',
    description:
      'messaging inbox row — outbound preview prefix for a message a human operator sent but whom this deploy cannot name (never used for the bot, and never for the viewer, who gets “You”)',
  },
  automationStopBotAction: {
    defaultMessage: 'Stop bot',
    description: 'case overview — WhatsApp card, stop-the-assistant button',
  },
  automationStopBotBody: {
    defaultMessage:
      'You take this conversation over, and the assistant stops replying on it. If nobody holds the case yet, it is assigned to you. Replies already dispatched may still send. The configured bot is left in place — it resumes if the conversation is handed back.',
    description: 'case overview — stop-bot confirmation body',
  },
  automationStopBotConfirm: {
    defaultMessage: 'Stop bot',
    description: 'case overview — stop-bot confirmation button',
  },
  automationStopBotSuccess: {
    defaultMessage: 'Assistant stopped for this conversation',
    description: 'case overview — stop-bot success toast',
  },
  automationStopBotTitle: {
    defaultMessage: 'Stop the assistant?',
    description: 'case overview — stop-bot confirmation title',
  },
  awaitingCustomerHint: {
    defaultMessage: 'Awaiting customer — consider a follow-up.',
    description: 'messaging soft hint shown when our last reply has gone unanswered',
  },
  backToInbox: {
    defaultMessage: 'Back to inbox',
    description: 'messaging mobile thread header — back to the conversation list',
  },
  blockedBanner: {
    defaultMessage:
      'An earlier message couldn’t be sent. {count, plural, one {# message is} other {# messages are}} on hold behind it until it goes through.',
    description:
      'messaging thread — head-of-line block: later outbound messages are queued behind an earlier one that is stuck retrying',
  },
  botName: {
    defaultMessage: 'Dolpheen Assistant',
    description: 'messaging — display name of the AI assistant',
  },
  botOwnedBody: {
    defaultMessage:
      "You're viewing live. The assistant escalates to a person when it's unsure, when the customer asks, or after a few turns.",
    description: 'messaging composer — bot-owned bar body',
  },
  botOwnedTitle: {
    defaultMessage: '{botName} is handling this conversation',
    description: 'messaging composer — bot-owned bar title',
  },
  callbackLine: {
    defaultMessage: 'Callback: {time}{bookedBy, select, none {} other { (booked by {bookedBy})}}',
    description: "messaging context — the linked case's next open reminder (a booked callback)",
  },
  caseActionsMenu: {
    defaultMessage: 'More case actions',
    description: 'messaging context rail — overflow menu beside the linked-case section label',
  },
  caseStageLabelTitle: {
    defaultMessage: 'Case {code}: {stage} · {type}',
    description: 'messaging inbox row — tooltip on the case stage label',
  },
  channelLabel: {
    defaultMessage: 'Channel',
    description: 'messaging context field',
  },
  closeAction: {
    defaultMessage: 'Close',
    description: 'messaging thread header close button tooltip',
  },
  composerAttach: {
    defaultMessage: 'Attach',
    description: 'messaging composer attach button tooltip',
  },
  composerPlaceholder: {
    defaultMessage: 'Type a message',
    description: 'messaging composer input placeholder',
  },
  composerSendHint: {
    defaultMessage: 'Press Enter to send · Shift+Enter for a new line',
    description: 'messaging composer hint',
  },
  connectionLive: {
    defaultMessage: 'Connected · live',
    description: 'messaging connection status',
  },
  connectionReconnecting: {
    defaultMessage: 'Reconnecting…',
    description: 'messaging connection status',
  },
  connectionSyncing: {
    defaultMessage: 'Catching up…',
    description: 'messaging connection status',
  },
  contextNoSelection: {
    defaultMessage:
      'Pick a conversation from the inbox to see its owner, linked cases and quick actions.',
    description: 'messaging context pane — body shown when no conversation is open',
  },
  contextNoSelectionTitle: {
    defaultMessage: 'No conversation selected',
    description: 'messaging context pane — title shown when no conversation is open',
  },
  contextTitle: {
    defaultMessage: 'Conversation',
    description: 'messaging context pane title',
  },
  contextUnavailable: {
    defaultMessage: "Conversation details couldn't be loaded.",
    description: 'messaging context pane — conversation failed to load',
  },
  createCaseAction: {
    defaultMessage: 'Create case from this thread',
    description: 'messaging context rail — promote a caseless triage thread to a case',
  },
  createCaseConfirm: {
    defaultMessage: 'Create case',
    description: 'messaging create-case-from-conversation dialog confirm button',
  },
  createCaseCustomerHelp: {
    defaultMessage:
      'This thread is with {phone}. Pick the customer it belongs to, or create a new one.',
    description: 'messaging create-case-from-conversation dialog — customer picker hint',
  },
  createCaseCustomerLabel: {
    defaultMessage: 'Customer',
    description: 'messaging create-case-from-conversation dialog — customer field',
  },
  createCaseHandedOverBody: {
    defaultMessage: 'Case #{code} was created, and this conversation is now with {name}.',
    description: 'messaging create-case-from-conversation — thread changed hands',
  },
  createCaseHandedOverTitle: {
    defaultMessage: 'The conversation changed hands',
    description: 'messaging create-case-from-conversation — hand-over result title',
  },
  createCaseHandedOverUnassigned: {
    defaultMessage:
      'Case #{code} was created, but no operator was available to take it — it is waiting unassigned, and so is this conversation.',
    description: 'messaging create-case-from-conversation — empty pool result',
  },
  createCaseHandedOverUnknown: {
    defaultMessage:
      'Case #{code} was created, and this conversation changed hands — but we could not read who has it now. Refresh to see where it went.',
    description: 'messaging create-case-from-conversation — hand-over with a stale thread snapshot',
  },
  createCaseLadderNotice: {
    defaultMessage:
      'The case goes to whoever already handles this customer for that case type. If nobody does, it stays with whoever is holding this conversation — otherwise it goes to the pool. The conversation follows the case.',
    description: 'messaging create-case-from-conversation dialog — assignment ladder notice',
  },
  createCaseNewCustomerHelp: {
    defaultMessage: 'The new customer gets this thread’s number, {phone}.',
    description: 'messaging create-case-from-conversation dialog — new customer name help',
  },
  createCaseNewCustomerNameLabel: {
    defaultMessage: 'New customer’s name',
    description: 'messaging create-case-from-conversation dialog — new customer name field',
  },
  createCaseNewCustomerNamedOption: {
    defaultMessage: 'New customer: {name}',
    description: 'messaging create-case-from-conversation dialog — create-customer picker entry',
  },
  createCaseNewCustomerOption: {
    defaultMessage: 'New customer',
    description: 'messaging create-case-from-conversation dialog — create-customer picker entry',
  },
  createCaseNoCaseTypes: {
    defaultMessage:
      'A case needs a type, and none is available to you. Either there is no active case type, or none is assigned to your team. An administrator can fix either one.',
    description: 'messaging create-case-from-conversation dialog — viewer has no case types',
  },
  createCaseNoteHelp: {
    defaultMessage: 'Optional. Recorded on the new case’s timeline.',
    description: 'messaging create-case-from-conversation dialog — note help',
  },
  createCaseNoteLabel: {
    defaultMessage: 'Note',
    description: 'messaging create-case-from-conversation dialog — note field',
  },
  createCaseOpenCase: {
    defaultMessage: 'Open the case',
    description: 'messaging create-case-from-conversation — link to the new case',
  },
  createCaseReasonEngagement: {
    defaultMessage:
      'They already handle this customer for this case type, so the case — and the conversation with it — went to them.',
    description: 'messaging create-case-from-conversation — why the engagement operator won',
  },
  createCaseReasonSimilarity: {
    defaultMessage:
      'They already hold this customer’s most recent case — or the case of a customer record we suspect is the same person — so this one, and the conversation with it, went to them.',
    description: 'messaging create-case-from-conversation — why the similarity rung won',
  },
  createCaseSuccess: {
    defaultMessage: 'Case #{code} created from this conversation',
    description: 'messaging create-case-from-conversation success notification',
  },
  createCaseTitle: {
    defaultMessage: 'Create a case from this conversation',
    description: 'messaging create-case-from-conversation dialog title',
  },
  createOtherCaseAction: {
    defaultMessage: 'Create a different case',
    description:
      'messaging context rail — open a case of another type for this customer, unbound to this thread',
  },
  createOtherCaseHelp: {
    defaultMessage:
      'For a case this number does not serve. It opens its own conversation on its own number, and this thread stays where it is.',
    description: 'messaging context rail — what creating a different case does',
  },
  createOtherCaseSuccess: {
    defaultMessage: 'Case {code} created',
    description: 'messaging context rail — a case of another type was created',
  },
  customerProfileAction: {
    defaultMessage: 'Open customer profile',
    description: 'messaging context rail — link out to the customer detail page',
  },
  customerTitle: {
    defaultMessage: 'Customer',
    description: 'messaging context section',
  },
  dayToday: {
    defaultMessage: 'Today',
    description: 'messaging timeline day separator — today',
  },
  dayYesterday: {
    defaultMessage: 'Yesterday',
    description: 'messaging timeline day separator — yesterday',
  },
  dedupeNotice: {
    defaultMessage: 'Duplicate ignored — that message is already sending.',
    description: 'messaging composer dedupe notice',
  },
  detailsAction: {
    defaultMessage: 'Details',
    description: 'messaging mobile thread header — open the conversation details sheet',
  },
  dismissAction: {
    defaultMessage: 'Dismiss',
    description: 'messaging unassigned triage row — spam-drop conversation button',
  },
  dismissError: {
    defaultMessage: 'Could not update the conversation',
    description: 'messaging dismiss/un-dismiss error notification',
  },
  dismissSuccess: {
    defaultMessage: 'Conversation updated',
    description: 'messaging dismiss/un-dismiss success notification',
  },
  documentLabel: {
    defaultMessage: 'Document',
    description: 'messaging document attachment label / fallback',
  },
  emptyInbox: {
    defaultMessage:
      'Your inbox is empty. New conversations appear when a case you own gets a WhatsApp reply.',
    description: 'messaging empty inbox',
  },
  emptyInboxFiltered: {
    defaultMessage: 'Nothing matches this filter.',
    description: 'messaging empty inbox filtered',
  },
  emptyInboxTitle: {
    defaultMessage: 'No conversations',
    description: 'messaging empty conversation list title',
  },
  emptyRecent: {
    defaultMessage: 'No recent conversations for this view.',
    description: 'messaging empty recent/history list',
  },
  emptyTeamInbox: {
    defaultMessage: 'This operator’s inbox is clear.',
    description: 'messaging team inbox — the chosen operator has no conversations',
  },
  emptyThreadBody: {
    defaultMessage: 'Pick a conversation from your inbox to read the thread and reply.',
    description: 'messaging empty thread body',
  },
  emptyThreadTitle: {
    defaultMessage: 'Select a conversation',
    description: 'messaging empty thread title',
  },
  emptyUnassigned: {
    defaultMessage: 'Triage is clear — no unassigned conversations waiting.',
    description: 'messaging empty unassigned queue',
  },
  failureHintBilling: {
    defaultMessage:
      'The WhatsApp business account has a payment problem, so WhatsApp refuses every send. Fix the payment method in Meta Business Manager.',
    description:
      'messaging permanent failure hint — Meta codes 131042 / 141006 (WABA payment issue)',
  },
  failureHintExperiment: {
    defaultMessage:
      'WhatsApp is withholding marketing templates from this number as part of a Meta experiment. It cannot be overridden. Reach the customer another way (call or SMS) and ask them to message us first; after that templates and replies work.',
    description:
      'messaging permanent failure hint — Meta code 130472 (marketing message experiment)',
  },
  failureHintInvalidParameter: {
    defaultMessage:
      "Meta rejected a parameter of this send — usually the customer's phone number is not a valid WhatsApp number. Check the number before trying again.",
    description:
      'messaging permanent failure hint — Meta codes 100 / 131009 (invalid parameter, read as an invalid recipient number)',
  },
  failureHintMarketingLimit: {
    defaultMessage:
      'WhatsApp limits how many marketing templates a person receives from all businesses, and this number has hit that limit. Retry after at least 24 hours; an immediate retry fails again.',
    description:
      'messaging permanent failure hint — Meta code 131049 (per-user marketing template limit)',
  },
  failureHintMedia: {
    defaultMessage:
      'WhatsApp could not process the attached media (unsupported format, too large, or the file could not be fetched). Try a different file.',
    description: 'messaging permanent failure hint — Meta codes 131052 / 131053 (media error)',
  },
  failureHintOptedOut: {
    defaultMessage:
      'The customer opted out of marketing messages from us. Only a reply inside an open 24-hour window or a utility template can reach them.',
    description: 'messaging permanent failure hint — Meta code 131050 (user stopped marketing)',
  },
  failureHintTemplateDisabled: {
    defaultMessage: 'Meta disabled this template. Use another template.',
    description: 'messaging permanent failure hint — Meta code 132016 (template disabled)',
  },
  failureHintTemplateMissing: {
    defaultMessage:
      "The template does not exist or is not approved for this channel and language. Pick another template, or ask an admin to sync the channel's templates.",
    description:
      'messaging permanent failure hint — Meta code 132001 (template missing / unapproved)',
  },
  failureHintTemplateParams: {
    defaultMessage:
      "The template's parameters did not match what Meta expects (count or format). Report this to an admin; retrying will not help.",
    description:
      'messaging permanent failure hint — Meta codes 131008 / 132000 / 132012 (template parameter mismatch)',
  },
  failureHintTemplatePaused: {
    defaultMessage:
      'Meta paused this template because of low quality ratings. Wait for it to resume or use another template.',
    description: 'messaging permanent failure hint — Meta code 132015 (template paused)',
  },
  failureHintTemplatePolicy: {
    defaultMessage:
      "The template's content violates Meta's format policy. Report this to an admin.",
    description: 'messaging permanent failure hint — Meta code 132007 (template format policy)',
  },
  failureHintTemplateTooLong: {
    defaultMessage:
      'The template text is too long once the parameters are filled in. Use shorter values or another template.',
    description: 'messaging permanent failure hint — Meta code 132005 (hydrated template too long)',
  },
  failureHintUndeliverable: {
    defaultMessage:
      'WhatsApp cannot deliver to this number: it is not on WhatsApp, has not accepted the latest terms, or has blocked us.',
    description: 'messaging permanent failure hint — Meta code 131026 (message undeliverable)',
  },
  failureHintWhy: {
    defaultMessage: 'Why did this fail?',
    description:
      'messaging permanent failure — accessible label of the info icon that opens the per-code hint',
  },
  failureParked: {
    defaultMessage: 'Delivery given up after repeated attempts. Needs an admin replay or skip.',
    description: 'messaging parked failure — retry budget exhausted, admin action required',
  },
  failurePermanent: {
    defaultMessage: "Not sent. {reason} · Won't retry automatically.",
    description: 'messaging permanent failure',
  },
  failurePermanentDefault: {
    defaultMessage: 'This message could not be sent.',
    description: 'messaging permanent failure default reason',
  },
  failureRetryable: {
    defaultMessage: "Still trying… WhatsApp is temporarily unavailable. We'll keep retrying.",
    description: 'messaging retryable failure',
  },
  failureRetryableNextAttempt: {
    defaultMessage: 'Next attempt at {time}.',
    description: 'messaging retryable failure — scheduled next automatic attempt',
  },
  failureTerminal: {
    defaultMessage: 'Gave up after several attempts. Needs your attention.',
    description: 'messaging terminal failure',
  },
  filterAll: {
    defaultMessage: 'All',
    description: 'messaging inbox filter',
  },
  filterAttention: {
    defaultMessage: 'Needs attention',
    description: 'messaging inbox filter',
  },
  filterClosing: {
    defaultMessage: 'Closing',
    description: 'messaging inbox filter',
  },
  filterOwesReply: {
    defaultMessage: 'Awaiting our reply',
    description: 'messaging inbox filter',
  },
  filterUnread: {
    defaultMessage: 'Unread',
    description: 'messaging inbox filter',
  },
  flagClosingSoon: {
    defaultMessage: 'Window closing soon',
    description: 'messaging inbox row flag — window closing soon',
  },
  flagNeedsAttention: {
    defaultMessage: 'Needs attention',
    description: 'messaging inbox row flag — needs attention',
  },
  flagOwesReply: {
    defaultMessage: 'Awaiting our reply',
    description: 'messaging inbox row flag — we owe this customer a reply',
  },
  flagSafetyFlagged: {
    defaultMessage: 'Flagged wording',
    description: 'messaging inbox row flag — outbound safety-term match on this conversation',
  },
  flagSafetyFlaggedHint: {
    defaultMessage:
      'A message on this conversation matched the safety-word list. This mark is for review and never clears — replying or archiving will not remove it.',
    description:
      'messaging inbox row flag tooltip — what the safety mark means and that it is permanent',
  },
  flagUnread: {
    defaultMessage: 'Unread messages',
    description: 'messaging inbox row flag — unread dot tooltip',
  },
  handbackAction: {
    defaultMessage: 'Hand back to AI',
    description: 'messaging ownership action',
  },
  handbackBody: {
    defaultMessage:
      'This conversation will be handled by {botName} again. It replies automatically and escalates back to a person if the customer asks, if it is unsure, or after a few turns. The full history stays in this thread.',
    description: 'messaging handback confirmation body',
  },
  handbackConfirm: {
    defaultMessage: 'Hand back',
    description: 'messaging handback confirmation button',
  },
  handbackNoPlaybook: {
    defaultMessage:
      'No bot is active on this conversation, so handing it back would leave the customer unanswered.',
    description:
      'messaging handback — tooltip on the disabled action when no playbook bot is active',
  },
  handbackSuccess: {
    defaultMessage: 'Conversation handed back to {botName}',
    description: 'messaging handback success notification',
  },
  handbackTitle: {
    defaultMessage: 'Hand back to {botName}?',
    description: 'messaging handback confirmation title',
  },
  handoffBack: {
    defaultMessage: 'Handed back to {botName}',
    description: 'messaging handoff timeline — operator handed back to the bot',
  },
  handoffBannerDismiss: {
    defaultMessage: 'Dismiss',
    description: 'messaging escalation banner dismiss action',
  },
  handoffComplete: {
    defaultMessage: '{botName} finished and handed off',
    description: 'messaging handoff timeline — planned handoff (screening done)',
  },
  handoffCompleteOutcome: {
    defaultMessage: '{botName} finished — {outcome}',
    description: 'messaging handoff timeline — planned handoff with the playbook outcome label',
  },
  handoffEscalatedBrainUnresponsive: {
    defaultMessage: '{botName} handed off — the assistant was unresponsive',
    description: 'messaging handoff timeline — watchdog escalation',
  },
  handoffEscalatedCustomer: {
    defaultMessage: '{botName} handed off — the customer asked for a person',
    description: 'messaging handoff timeline — customer-requested escalation',
  },
  handoffEscalatedDefault: {
    defaultMessage: '{botName} handed off to a person',
    description: 'messaging handoff timeline — escalation with an unknown reason',
  },
  handoffEscalatedLowConfidence: {
    defaultMessage: '{botName} handed off — it was not confident it could help',
    description: 'messaging handoff timeline — low-confidence escalation',
  },
  handoffEscalatedTurnLimit: {
    defaultMessage: '{botName} handed off — the conversation limit was reached',
    description: 'messaging handoff timeline — turn-limit escalation',
  },
  handoffMessageSkipped: {
    defaultMessage: 'A stuck message was skipped',
    description: 'messaging timeline — skipped message audit entry without an operator name',
  },
  handoffMessageSkippedBy: {
    defaultMessage: '{name} skipped a stuck message',
    description: 'messaging timeline — skipped message audit entry naming the operator',
  },
  handoffTakeOver: {
    defaultMessage: 'Taken over from {botName}',
    description: 'messaging handoff timeline — operator took over from the bot',
  },
  historyReadOnly: {
    defaultMessage: 'View only — you no longer handle this conversation.',
    description:
      'messaging thread — read-only footer on a previously-handled (Recent) conversation, current owner unknown',
  },
  historyReadOnlyWith: {
    defaultMessage: 'View only — now handled by {name}.',
    description:
      'messaging thread — read-only footer on a previously-handled (Recent) conversation, naming the current owner',
  },
  inboxFilterStageComplete: {
    defaultMessage: 'Complete',
    description: 'messaging inbox stage filter chip — cases already complete',
  },
  inboxFilterStageOnboarding: {
    defaultMessage: 'Onboarding',
    description: 'messaging inbox stage filter chip — cases being onboarded',
  },
  inboxFilterStageServicing: {
    defaultMessage: 'Servicing',
    description: 'messaging inbox stage filter chip — cases already being serviced',
  },
  inboxFilterStageUncontacted: {
    defaultMessage: 'New lead',
    description: 'messaging inbox stage filter chip — uncontacted new-lead cases',
  },
  inboxLoadError: {
    defaultMessage: 'Check your connection and retry.',
    description: 'messaging inbox load failure body',
  },
  inboxLoadErrorTitle: {
    defaultMessage: "Couldn't load conversations",
    description: 'messaging inbox load failure title',
  },
  insightDismiss: {
    defaultMessage: 'Dismiss',
    description: 'messaging oversight insight dismiss action',
  },
  insightFileConfirm: {
    defaultMessage: 'Add',
    description: 'messaging oversight insight file-document confirm dialog — submit button',
  },
  insightFileConfirmBody: {
    defaultMessage: 'Add {label}?',
    description:
      'messaging oversight insight file-document confirm dialog body, {label} is the suggested-action label',
  },
  insightFileConfirmTitle: {
    defaultMessage: 'File document',
    description: 'messaging oversight insight file-document confirm dialog title',
  },
  insightFilePending: {
    defaultMessage: 'Filing…',
    description:
      'messaging oversight insight — chip label while a file-document confirmation is in flight (reserved, not yet finalized)',
  },
  insightFileSuccess: {
    defaultMessage: 'Document filed.',
    description: 'messaging oversight insight file-document confirm success toast',
  },
  insightFiledLabel: {
    defaultMessage: 'Filed',
    description:
      'messaging oversight insight — chip label once the file-document action was confirmed',
  },
  insightsTitle: {
    defaultMessage: 'Bot insights',
    description: 'messaging context section — oversight insights panel',
  },
  interactiveChoiceHint: {
    defaultMessage: 'Tapped option',
    description: 'messaging bubble — caption on an inbound structured button/list reply',
  },
  interactiveOptionsLabel: {
    defaultMessage: 'Options',
    description: 'messaging bubble — fallback label over an outbound option list',
  },
  // ---- image lightbox (in-app preview of a thread image; point 2, 2026-09-16) ----
  lightboxClose: {
    defaultMessage: 'Close preview',
    description: 'messaging image lightbox — close button aria label',
  },
  lightboxCounter: {
    defaultMessage: '{index} of {total}',
    description: 'messaging image lightbox — position counter (1-based index of total images)',
  },
  lightboxDownload: {
    defaultMessage: 'Download',
    description: 'messaging image lightbox — download the original file',
  },
  lightboxLoading: {
    defaultMessage: 'Loading image…',
    description: 'messaging image lightbox — image bytes not resolved yet',
  },
  lightboxNext: {
    defaultMessage: 'Next image',
    description: 'messaging image lightbox — next button aria label',
  },
  lightboxOpen: {
    defaultMessage: 'Open image preview',
    description: 'messaging image thumbnail — click opens the in-app lightbox',
  },
  lightboxPrevious: {
    defaultMessage: 'Previous image',
    description: 'messaging image lightbox — previous button aria label',
  },
  lightboxTitle: {
    defaultMessage: 'Image preview',
    description: 'messaging image lightbox — dialog title (screen readers)',
  },
  lightboxUnavailable: {
    defaultMessage: 'This image is no longer available.',
    description:
      'messaging image lightbox — terminal placeholder when the bytes could not be resolved',
  },
  linkedCaseTitle: {
    defaultMessage: 'Linked {count, plural, =0 {case} one {case} other {cases}}',
    description: 'messaging context section',
  },
  listYesterdayShort: {
    defaultMessage: 'Yest.',
    description: 'messaging inbox row timestamp — yesterday, abbreviated',
  },
  loadOlderMessages: {
    defaultMessage: 'Load earlier messages',
    description: 'messaging thread — button to fetch older messages',
  },
  loadingOlderMessages: {
    defaultMessage: 'Loading…',
    description: 'messaging thread — older-messages fetch in progress',
  },
  messagesFailedBody: {
    defaultMessage:
      'The message history could not be loaded. Reload the page — if it keeps failing, report it, because the messages themselves are safe.',
    description: 'messaging thread — history query failed body',
  },
  messagesFailedTitle: {
    defaultMessage: 'Messages could not be loaded',
    description: 'messaging thread — history query failed title',
  },
  nameLabel: {
    defaultMessage: 'Name',
    description: 'messaging context field',
  },
  noLinkedCase: {
    defaultMessage: 'No case linked to this conversation yet.',
    description: 'messaging context empty',
  },
  noMessagesBody: {
    defaultMessage:
      "This conversation is linked to a case but the customer hasn't messaged on this number yet.",
    description: 'messaging empty timeline body',
  },
  noMessagesTitle: {
    defaultMessage: 'No messages yet',
    description: 'messaging empty timeline title',
  },
  notificationNewMessage: {
    defaultMessage: 'New WhatsApp message',
    description: 'browser notification body fallback',
  },
  operatorPickerEmpty: {
    defaultMessage: 'No operators found',
    description: 'messaging inbox operator picker — no results',
  },
  operatorPickerPlaceholder: {
    defaultMessage: 'View another inbox…',
    description: 'messaging inbox operator picker placeholder (manager cross-operator view)',
  },
  operatorRowAssignee: {
    defaultMessage: 'Assigned to {name}',
    description: 'messaging inbox row — conversation assignee (manager view)',
  },
  ownershipBotSubtitle: {
    defaultMessage: 'Handling automatically',
    description: 'messaging ownership card — bot subtitle',
  },
  ownershipTitle: {
    defaultMessage: 'Ownership',
    description: 'messaging context section',
  },
  pickUpAction: {
    defaultMessage: 'Pick up',
    description: 'messaging unassigned triage row — claim conversation button',
  },
  pickUpBlockedAction: {
    defaultMessage: 'Assign on case',
    description:
      'messaging unassigned triage row — link to the case, shown instead of Pick up when the row already has one',
  },
  pickUpBlockedHint: {
    // Spelled out rather than hinted at: the operator's instinct here is to
    // keep pressing Pick up, and on a touch device (no tooltip) the label
    // above is all they get, so the sentence has to survive being unread.
    defaultMessage:
      'This conversation belongs to case #{code}, so it cannot be picked up here. Open the case and assign it to an operator.',
    description:
      'messaging unassigned triage row — why an engaged row cannot be claimed, and the recovery',
  },
  pickUpError: {
    defaultMessage: 'Could not pick up the conversation',
    description: 'messaging pick-up error notification',
  },
  pickUpSuccess: {
    // The holder is a rung on the case-assignment ladder, so a case created
    // here lands on them unless the customer already has an operator for that
    // case type. That product rule is what the second clause promises.
    defaultMessage: 'You picked up the conversation — a case opened on it will come to you',
    description: 'messaging pick-up success notification',
  },
  previewAudio: {
    defaultMessage: 'Audio message',
    description: 'messaging conversation preview — audio message',
  },
  previewEmpty: {
    defaultMessage: 'No messages yet',
    description: 'messaging conversation preview — no messages',
  },
  previewInteractive: {
    defaultMessage: 'Interactive message',
    description: 'messaging conversation preview — interactive message',
  },
  previewMessageDeleted: {
    defaultMessage: 'Message deleted',
    description: 'messaging conversation preview — deleted message',
  },
  previewPhoto: {
    defaultMessage: 'Photo',
    description: 'messaging conversation preview — photo/image',
  },
  previewReaction: {
    defaultMessage: 'Reaction',
    description: 'messaging conversation preview — reaction',
  },
  previewSticker: {
    defaultMessage: 'Sticker',
    description: 'messaging conversation preview — sticker',
  },
  previewTemplateOpener: {
    defaultMessage: 'Opener sent',
    description:
      'messaging conversation preview — a template went out and the customer has never replied',
  },
  previewTemplateReopen: {
    defaultMessage: 'Window re-open message sent',
    description:
      'messaging conversation preview — a template went out after the 24-hour window had closed',
  },
  previewUnsupported: {
    defaultMessage: 'Unsupported message',
    description: 'messaging conversation preview — unsupported message',
  },
  previewVideo: {
    defaultMessage: 'Video',
    description: 'messaging conversation preview — video',
  },
  quickActionCall: {
    defaultMessage: 'Call',
    description: 'messaging quick action',
  },
  quickActionCopyNumber: {
    defaultMessage: 'Copy number',
    description: 'messaging quick action',
  },
  quickActionsTitle: {
    defaultMessage: 'Quick actions',
    description: 'messaging context section',
  },
  quickReplyAddAction: {
    defaultMessage: 'New quick reply',
    description: 'messaging quick-reply manager — start a new snippet',
  },
  quickReplyBodyCounter: {
    defaultMessage: '{count} / {max} characters',
    description: 'messaging quick-reply editor — body length counter',
  },
  quickReplyBodyLabel: {
    defaultMessage: 'Message',
    description: 'messaging quick-reply editor — the text that gets inserted',
  },
  quickReplyDeleteConfirm: {
    defaultMessage: 'Delete this?',
    description: 'messaging quick-reply manager — inline delete confirmation',
  },
  quickReplyDeleted: {
    defaultMessage: 'Quick reply deleted.',
    description: 'messaging quick-reply manager — delete success notification',
  },
  quickReplyEmpty: {
    defaultMessage: 'No quick replies yet. Add one, then type / in the message box to drop it in.',
    description: 'messaging quick-reply manager — empty state',
  },
  quickReplyManageAction: {
    defaultMessage: 'Manage quick replies',
    description: 'messaging quick-reply picker — footer link to the editor',
  },
  quickReplyManagerFootNote: {
    defaultMessage:
      'Quick replies are yours alone and are sent as ordinary messages, so they only work while the 24-hour window is open.',
    description: 'messaging quick-reply manager — footer explanation',
  },
  quickReplyManagerSubtitle: {
    defaultMessage: 'Only you can see and use these',
    description: 'messaging quick-reply manager — header note',
  },
  quickReplyManagerTitle: {
    defaultMessage: 'Quick replies',
    description: 'messaging quick-reply manager — dialog title',
  },
  quickReplyPickerHint: {
    defaultMessage: '↑↓ to choose · Enter to insert · Esc to close',
    description: 'messaging quick-reply picker — keyboard hint',
  },
  quickReplySaved: {
    defaultMessage: 'Quick reply saved.',
    description: 'messaging quick-reply manager — save success notification',
  },
  quickReplyShortcutHelp: {
    defaultMessage: 'Letters, numbers, - and _ only. This is what you type after the /.',
    description: 'messaging quick-reply editor — shortcut field helper',
  },
  quickReplyShortcutLabel: {
    defaultMessage: 'Shortcut',
    description: 'messaging quick-reply editor — the /token field label',
  },
  quickReplyToolTitle: {
    defaultMessage: 'Quick replies — type / in the message box',
    description: 'messaging composer tools — quick-reply manager button tooltip',
  },
  reassignedBody: {
    defaultMessage: 'This conversation was reassigned and is no longer in your inbox.',
    description:
      'messaging thread — the open conversation was reassigned away from this operator (new owner unknown / unassigned)',
  },
  reassignedLeave: {
    defaultMessage: 'Back to inbox',
    description:
      'messaging thread — leave the reassigned-conversation panel and return to the inbox',
  },
  reassignedTitle: {
    defaultMessage: 'Conversation reassigned',
    description:
      'messaging thread — title of the panel shown when the open conversation is reassigned away',
  },
  reassignedToBody: {
    defaultMessage: 'This conversation was reassigned to {name} and is no longer in your inbox.',
    description: 'messaging thread — the open conversation was reassigned to a named operator',
  },
  retryAction: {
    defaultMessage: 'Retry now',
    description: 'messaging failure action',
  },
  retryTemplateHint: {
    defaultMessage: 'Re-send it from the template picker.',
    description: 'messaging failure hint — a failed template message cannot be retried in place',
  },
  safetyConfirmAction: {
    defaultMessage: 'Send anyway',
    description: 'messaging safety warning — confirm button on a flagged outbound message',
  },
  safetyConfirmBody: {
    defaultMessage:
      'Part of this message matches the safety-word list, and is highlighted below. Your message is back in the composer — go back to edit it, or send it as it is.',
    description: 'messaging safety warning — explanation above the operator’s own flagged text',
  },
  safetyConfirmNote: {
    defaultMessage:
      'Either way this conversation is marked for review, and that mark does not clear. Sending anyway records that you read this warning.',
    description:
      'messaging safety warning — footnote: the acknowledgement is provenance, not a bypass',
  },
  safetyConfirmTitle: {
    defaultMessage: 'Check this wording before sending',
    description: 'messaging safety warning — dialog title',
  },
  safetyFieldCaption: {
    defaultMessage: 'Caption',
    description: 'messaging safety warning — label for the attachment caption that was flagged',
  },
  safetyFieldFilename: {
    defaultMessage: 'File name',
    description: 'messaging safety warning — label for the attachment file name that was flagged',
  },
  safetyFieldTemplate: {
    defaultMessage: 'Template details',
    description: 'messaging safety warning — label for the template fill that was flagged',
  },
  safetyFieldText: {
    defaultMessage: 'Message',
    description: 'messaging safety warning — label for the message body that was flagged',
  },
  scopeMyInbox: {
    defaultMessage: 'My inbox',
    description: 'messaging inbox scope toggle — operator’s own conversations',
  },
  scopeRecent: {
    defaultMessage: 'Recent',
    description:
      'messaging inbox scope toggle — read-only conversations the operator previously handled',
  },
  scopeTeam: {
    defaultMessage: 'Supervised',
    description:
      'messaging inbox scope toggle — read-only view of another operator’s inbox (team-lead / supervisor)',
  },
  scopeUnassigned: {
    defaultMessage: 'Triage',
    description: 'messaging inbox scope toggle — caseless triage queue (support)',
  },
  screeningOutcomeLabel: {
    defaultMessage: 'Outcome',
    description: 'messaging screening summary — outcome chip label',
  },
  screeningSummaryTitle: {
    defaultMessage: 'Screening summary',
    description: 'messaging context section — bot screening summary panel',
  },
  searchPlaceholder: {
    defaultMessage: 'Search by phone number',
    description: 'messaging inbox search placeholder',
  },
  senderExternal: {
    defaultMessage: 'via WhatsApp Business app',
    description: 'messaging external sender attribution',
  },
  senderSystem: {
    defaultMessage: 'Sent automatically',
    description: 'messaging attribution — outbound message sent by the platform, not a person',
  },
  senderYou: {
    defaultMessage: 'You',
    description:
      'messaging thread attribution — an outbound message this viewer sent themselves, on the bubble header and on a quoted reply (a colleague’s message shows their name instead)',
  },
  setCustomerAction: {
    defaultMessage: 'Not this customer?',
    description: 'messaging context pane — open the correct-customer dialog',
  },
  setCustomerConfirm: {
    defaultMessage: 'Move to this customer',
    description: 'messaging set-conversation-customer dialog confirm',
  },
  setCustomerHelp: {
    defaultMessage: 'This thread is with {phone}. Pick the customer it really belongs to.',
    description: 'messaging set-conversation-customer dialog — customer picker hint',
  },
  setCustomerLabel: {
    defaultMessage: 'Customer',
    description: 'messaging set-conversation-customer dialog — customer picker label',
  },
  setCustomerPhoneMoveNotice: {
    defaultMessage:
      'This number moves to the chosen customer and is removed from whoever holds it now. Otherwise the next message would file the thread back where it was.',
    description: 'messaging set-conversation-customer dialog — phone move warning',
  },
  setCustomerSuccess: {
    defaultMessage: 'Conversation moved to the chosen customer',
    description: 'messaging set-conversation-customer success toast',
  },
  setCustomerTitle: {
    defaultMessage: 'Correct whose conversation this is',
    description: 'messaging set-conversation-customer dialog title',
  },
  showDismissed: {
    defaultMessage: 'Show dismissed',
    description: 'messaging unassigned triage — reveal spam-dropped conversations toggle',
  },
  skipMessageAction: {
    defaultMessage: 'Skip message',
    description: 'messaging blocked banner — action offered to the assigned operator',
  },
  skipMessageBody: {
    defaultMessage:
      'This message will be marked permanently failed so {count, plural, one {the message behind it can send} other {the # messages behind it can send}}. The skipped message will not be delivered.',
    description: 'messaging blocked banner — skip confirmation explanation',
  },
  skipMessageConfirm: {
    defaultMessage: 'Skip message',
    description: 'messaging blocked banner — skip confirmation button',
  },
  skipMessageTitle: {
    defaultMessage: 'Skip this stuck message?',
    description: 'messaging blocked banner — skip confirmation title',
  },
  sortMostRecent: {
    defaultMessage: 'Most recent',
    description: 'messaging inbox sort',
  },
  sortUnreadFirst: {
    defaultMessage: 'Unread first',
    description: 'messaging inbox sort',
  },
  sourceLabel: {
    defaultMessage: 'Source',
    description:
      'messaging context pane — label of the referral-source row (where the customer came from)',
  },
  sourceNotResolved: {
    defaultMessage: 'not resolved',
    description:
      'messaging context pane — suffix after a referral code the store could not resolve (expired, or unavailable at first contact)',
  },
  sourceWeb: {
    defaultMessage: 'Web',
    description: 'messaging context pane — referral channel name for a code minted by the website',
  },
  systemDeleted: {
    defaultMessage: 'This message was deleted.',
    description: 'messaging deleted message line',
  },
  systemReactionToMessage: {
    defaultMessage: 'Customer reacted {emoji} to a message',
    description: 'messaging inbound reaction line (target not in view)',
  },
  systemReactionToYours: {
    defaultMessage: 'Customer reacted {emoji} to your message',
    description: 'messaging inbound reaction to an operator message',
  },
  systemSentCaption: {
    defaultMessage: 'Automatic — not sent by you',
    description: 'messaging bubble caption on a platform-sent outbound message',
  },
  systemUndelivered: {
    defaultMessage:
      "WhatsApp didn't deliver this {kind, select, image {photo} video {video} audio {voice message} document {document} sticker {sticker} other {message}} — it can't be opened. Ask the customer to send it again.",
    description:
      'messaging line for a message Meta dropped (unsupported, error 131051); kind is the dropped media type',
  },
  systemUnknownContact: {
    defaultMessage: 'Customer shared a contact card — it is not shown here.',
    description: 'messaging line for an inbound WhatsApp contact card',
  },
  systemUnknownLocation: {
    defaultMessage: 'Customer shared a location — it is not shown here.',
    description: 'messaging line for an inbound WhatsApp location',
  },
  systemUnsupported: {
    defaultMessage: 'Unsupported message — open the WhatsApp Business app to view it.',
    description: 'messaging unsupported message line',
  },
  takeOverAction: {
    defaultMessage: 'Take over',
    description: 'messaging take-over button',
  },
  takeOverBody: {
    defaultMessage:
      'You will reply yourself from here on. {botName} stops responding immediately; you can hand the conversation back at any time.',
    description: 'messaging take-over confirmation body',
  },
  takeOverConfirm: {
    defaultMessage: 'Take over',
    description: 'messaging take-over confirmation button',
  },
  takeOverSuccess: {
    defaultMessage: 'You took over from {botName}',
    description: 'messaging take-over success notification',
  },
  takeOverTitle: {
    defaultMessage: 'Take over from {botName}?',
    description: 'messaging take-over confirmation title',
  },
  teamInboxPickPrompt: {
    defaultMessage: 'Pick an operator to see their inbox.',
    description: 'messaging team inbox — empty state before an operator is chosen',
  },
  templateApprovedCount: {
    defaultMessage:
      '{count, plural, one {# approved template} other {# approved templates}} · synced from Meta Business Manager',
    description: 'messaging template library footnote',
  },
  templateBodyRetired: {
    defaultMessage: 'Sent using the “{name}” template, which is no longer available.',
    description:
      'messaging bubble — a template message whose template has since been deleted from Meta, so only its name is known',
  },
  templateBodyUnavailable: {
    defaultMessage: 'Template content unavailable',
    description: 'messaging bubble — a template message stored without any body text',
  },
  templateBrowse: {
    defaultMessage: 'Browse templates',
    description: 'messaging template action',
  },
  templateCaption: {
    defaultMessage: 'Template',
    description: 'messaging bubble caption — template message',
  },
  templateEmpty: {
    defaultMessage: 'No approved templates are available for this channel.',
    description: 'messaging template library empty state',
  },
  templateFillAgentName: {
    defaultMessage: 'Agent: {name}',
    description:
      'messaging template picker — one-click chip that fills a variable with the agent (assignee) name',
  },
  templateFillCustomerName: {
    defaultMessage: 'Customer: {name}',
    description:
      'messaging template picker — one-click chip that fills a variable with the customer name',
  },
  templateFillHint: {
    defaultMessage: 'Fill in every variable to send.',
    description: 'messaging template picker — incomplete variables note',
  },
  templateHeaderMedia: {
    defaultMessage:
      '{format, select, IMAGE {Image} VIDEO {Video} DOCUMENT {Document} LOCATION {Location} other {Media}} header',
    description:
      'messaging bubble — stand-in for a template media header whose bytes we do not hold (imported from Business Manager), so it cannot be drawn',
  },
  templateLoadError: {
    defaultMessage: "Couldn't load the template library. Check your connection and retry.",
    description: 'messaging template library load failure',
  },
  templatePickerSubtitle: {
    defaultMessage: 'Approved templates only · synced from Business Manager',
    description: 'messaging template picker subtitle',
  },
  templatePickerTitle: {
    defaultMessage: 'Send a template',
    description: 'messaging template picker title',
  },
  templatePreviewLabel: {
    defaultMessage: 'Preview',
    description: 'messaging template picker preview label',
  },
  templateSearchPlaceholder: {
    defaultMessage: 'Search templates',
    description: 'messaging template picker search placeholder',
  },
  templateSendAction: {
    defaultMessage: 'Send template',
    description: 'messaging template picker send button',
  },
  templateSendNote: {
    defaultMessage: 'Approved templates can be sent while the 24-hour window is closed.',
    description: 'messaging template picker — send availability note',
  },
  templateVariableHint: {
    defaultMessage: 'Variable {n}',
    description: 'messaging template picker variable input label',
  },
  templateVariablesLabel: {
    defaultMessage: 'Variables',
    description: 'messaging template picker variables label',
  },
  threadCountLabel: {
    defaultMessage: '{count, plural, one {# conversation} other {# conversations}}',
    description: 'messaging inbox count',
  },
  threadNoAccessBody: {
    defaultMessage:
      'This conversation was handled by another operator. Ask a manager or the case assignee if you need its history.',
    description: 'messaging thread — no read access body',
  },
  threadNoAccessTitle: {
    defaultMessage: 'You don’t have access to this conversation',
    description: 'messaging thread — no read access title',
  },
  threadUnavailableBody: {
    defaultMessage:
      "This conversation couldn't be loaded. It may have been reassigned or removed — pick another from your inbox.",
    description: 'messaging thread — conversation failed to load body',
  },
  threadUnavailableTitle: {
    defaultMessage: 'Conversation unavailable',
    description: 'messaging thread — conversation failed to load title',
  },
  tickDelivered: {
    defaultMessage: 'Delivered',
    description: 'messaging delivery status — delivered',
  },
  tickQueued: {
    defaultMessage: 'Queued',
    description: 'messaging delivery status — queued',
  },
  tickRead: {
    defaultMessage: 'Read',
    description: 'messaging delivery status — read',
  },
  tickSending: {
    defaultMessage: 'Sending',
    description: 'messaging delivery status — sending',
  },
  tickSent: {
    defaultMessage: 'Sent',
    description: 'messaging delivery status — sent',
  },
  toolActivityRead: {
    defaultMessage: '{botName} looked up: {tools}',
    description: 'messaging timeline — AI read one or more CRM tools this turn',
  },
  toolActivityWrite: {
    defaultMessage: '{botName} updated customer profile: {fields}',
    description: 'messaging timeline — AI applied customer profile fields',
  },
  toolActivityWriteFailed: {
    defaultMessage: '{botName} tried to update the customer profile — nothing was saved',
    description: 'messaging timeline — AI profile update applied no fields',
  },
  unassignedNotice: {
    defaultMessage: 'This conversation is not assigned. Assignment follows the linked case owner.',
    description: 'messaging unassigned notice',
  },
  unassignedReadOnly: {
    defaultMessage: 'View only — nobody is handling this yet. Pick it up to reply.',
    description:
      'messaging thread — read-only footer on an unassigned (triage) conversation; sending is assignee-only',
  },
  unassignedReadOnlyWithCase: {
    defaultMessage:
      'View only — nobody is handling this yet. It belongs to case #{code}, so the operator is chosen on the case.',
    description:
      'messaging thread — read-only footer on an unassigned conversation bound to a case, where pick-up is refused; the recovery link follows',
  },
  undismissAction: {
    defaultMessage: 'Un-dismiss',
    description: 'messaging unassigned triage row — restore spam-dropped conversation button',
  },
  windowClosed: {
    defaultMessage: '24-hour window closed',
    description: 'messaging window state',
  },
  windowClosedBody: {
    defaultMessage:
      'Free-form replies are paused. Send a pre-approved template, or wait for the customer to message again.',
    description: 'messaging window closed body',
  },
  windowClosing: {
    defaultMessage: 'Closing · {remaining}',
    description: 'messaging window state',
  },
  windowOpen: {
    defaultMessage: 'Open · {remaining} left',
    description: 'messaging window state',
  },
  windowTimeLeft: {
    defaultMessage: '{remaining} left in window',
    description: 'messaging composer footer — reply window time left',
  },
  windowUnopened: {
    defaultMessage: 'No reply yet',
    description:
      'messaging window state — the customer has never written, so no 24-hour window has opened',
  },
  windowUnopenedBody: {
    defaultMessage:
      'Free-form replies unlock once the customer writes back. Until then, only a pre-approved template can be sent.',
    description: 'messaging window never-opened body',
  },
  workspaceTitle: {
    defaultMessage: 'Messages',
    description: 'messaging workspace title',
  },
})
